import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "src/assets/images/close.png";
import ArrowRightIcon from "src/assets/images/arrow_right.png";
import { useLenis } from "src/context/LenisContext";
import { menuTextStyle, burgerLineStyle, fadeUp, menuItemStyle } from "src/styles";
import { useLocation, useNavigate } from "react-router-dom";

export type MenuType = { title: string; url: string; onClick?: string[] };

export const MainHeader: React.FC = () => {
  const theme = useTheme();
  const lenis = useLenis();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menusOpen, setMenusOpen] = useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [scrolled, setScrolled] = useState(false);

  const menus: MenuType[] = [
    { title: t("header.home"), url: "/" },
    { title: t("header.service"), url: "/" },
    { title: t("header.choose"), url: "/" },
    { title: t("header.works"), url: "/" },
    { title: t("header.recruit"), url: "/" },
    // { title: t("header.company"), url: "#company" },
    { title: t("header.privacy"), url: "/privacy" },
    { title: t("header.security"), url: "/security" },
  ];
  const location = useLocation();

  const onMenuClick = (idx: number, url: string) => {
    setMenusOpen(false);
    if (url === "/") {
      if (location.pathname === "/") {
        return lenis?.scrollTo(`#section-${idx}`);
      } else {
        return navigate(url);
      }
    } else {
      return window.open(url, "_blank");
    }
  };

  useEffect(() => {
    document.body.style.overflow = !isDesktop && menusOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isDesktop, menusOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Stack
      sx={{
        width: "calc(100% - 60px)",
        height: "65px",
        px: "30px",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        position: "fixed",
        zIndex: "99",
        backgroundColor: scrolled ? "#ffffff" : "transparent",
        transition: "background-color 2s ease",
        boxShadow: scrolled ? "0 2px 4px rgba(0, 0, 0, 0.05)" : "none",
      }}
    >
      <Box onClick={() => navigate("/")} sx={{ cursor: "pointer" }}>
        <Typography fontSize="28px" fontFamily="RacingSansOne" color="#1968e0">
          SmartLabs
        </Typography>
      </Box>
      <Stack sx={{ pr: "4vw", flexDirection: "row", gap: "40px", [theme.breakpoints.down("md")]: { display: "none" } }}>
        {menus.map((menu, idx) => (
          <Typography
            key={menu.title}
            fontSize="16px"
            fontWeight={500}
            onClick={() => onMenuClick(idx, menu.url)}
            sx={{
              ...menuItemStyle,
              ...menuTextStyle,
              color: "#323e59",
              m: 0,
              "&:hover": { color: "#1968e0", "&::after": { bgcolor: "#1968e0" } },
            }}
          >
            {menu.title}
          </Typography>
        ))}
      </Stack>
      <Box
        onClick={() => setMenusOpen(true)}
        sx={{
          [theme.breakpoints.up("md")]: { display: "none" },
          display: menusOpen ? "none" : "flex",
          gap: "8px",
          alignItems: "center",
          cursor: "pointer",
          pr: "2vw",
        }}
      >
        <Box sx={burgerLineStyle} />
        <Typography fontSize={14} fontWeight="bold" color="#323e59">
          Menu
        </Typography>
      </Box>
      <Box
        sx={{
          width: "calc(100% - 144px)",
          height: "100vh",
          padding: "96px 72px 0",
          position: "fixed",
          top: 65,
          left: 0,
          zIndex: 9999,
          background: "#ffffff",
          display: menusOpen ? "flex" : "none",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box onClick={() => setMenusOpen(false)} sx={{ position: "absolute", top: "-40px", right: 20, cursor: "pointer" }}>
          <img src={CloseIcon} alt="close" />
        </Box>
        {menus.map((menu, index) => (
          <Box
            key={menu.title}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => onMenuClick(index, menu.url)}
            sx={{ opacity: 0, animation: `${fadeUp} 0.6s ease-out forwards`, animationDelay: `${index * 0.1 + 0.2}s` }}
          >
            <Typography
              color="#323e59"
              fontSize="18px"
              fontWeight={400}
              width="fit-content"
              sx={{
                ...menuTextStyle,
                paddingBlock: "10px",
                [theme.breakpoints.down("sm")]: {
                  width: "100%",
                  fontSize: "18px",
                  paddingBlock: "7px",
                },
              }}
            >
              {menu.title}
            </Typography>
            <img src={ArrowRightIcon} alt="arrow right" />
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

export default MainHeader;
